/* ERS theme — shared behaviour (header, drawers, cart, search, modals). No dependencies. */
(() => {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};

  // Shopify HTML-escapes translations ("We'll" arrives as "We&#39;ll"); decode them once for use as plain text.
  const decodeEntities = (value) => {
    if (typeof value !== 'string' || !value.includes('&')) return value;
    const field = document.createElement('textarea');
    field.innerHTML = value;
    return field.value;
  };
  const strings = {};
  Object.keys(theme.strings || {}).forEach((key) => {
    strings[key] = decodeEntities(theme.strings[key]);
  });
  theme.strings = strings;

  /* ---------- Utilities ---------- */
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  };

  const FOCUSABLE =
    'a[href], area[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

  const visibleFocusable = (container) =>
    Array.from(container.querySelectorAll(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null || el === document.activeElement
    );

  const trapFocus = (container) => {
    const handler = (event) => {
      if (event.key !== 'Tab') return;
      const items = visibleFocusable(container);
      if (!items.length) {
        event.preventDefault();
        container.focus();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === container)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    container.addEventListener('keydown', handler);
    return () => container.removeEventListener('keydown', handler);
  };

  let scrollLocks = 0;
  const lockScroll = () => {
    scrollLocks += 1;
    document.body.classList.add('has-locked-scroll');
  };
  const unlockScroll = () => {
    scrollLocks = Math.max(0, scrollLocks - 1);
    if (!scrollLocks) document.body.classList.remove('has-locked-scroll');
  };

  const parseHTML = (text) => new DOMParser().parseFromString(text, 'text/html');

  const showToast = (message, timeout = 3200) => {
    const toast = document.getElementById('Toast');
    if (!toast || !message) return;
    toast.hidden = false;
    toast.textContent = '';
    clearTimeout(showToast.timer);
    clearTimeout(showToast.clearTimer);
    // Set the text a frame later so screen readers announce it as new content.
    requestAnimationFrame(() => {
      toast.textContent = message;
      toast.classList.add('is-visible');
    });
    showToast.timer = setTimeout(() => {
      toast.classList.remove('is-visible');
      showToast.clearTimer = setTimeout(() => {
        toast.textContent = '';
      }, 250);
    }, timeout);
  };

  const formatMoney = (cents, format = theme.moneyFormat || '${{amount}}') => {
    const value = Number(cents) / 100;
    const withDelimiters = (number, decimals, thousands = ',', decimal = '.') => {
      const fixed = number.toFixed(decimals).split('.');
      fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      switch (key) {
        case 'amount_no_decimals':
          return withDelimiters(value, 0);
        case 'amount_with_comma_separator':
          return withDelimiters(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator':
          return withDelimiters(value, 0, '.', ',');
        case 'amount_with_space_separator':
          return withDelimiters(value, 2, ' ', ',');
        case 'amount_with_apostrophe_separator':
          return withDelimiters(value, 2, "'", '.');
        default:
          return withDelimiters(value, 2);
      }
    });
  };

  window.ERS = Object.assign(window.ERS || {}, { debounce, trapFocus, showToast, formatMoney, lockScroll, unlockScroll, parseHTML });

  // Escape closes the most recently opened drawer or modal, wherever focus is.
  const openLayers = [];
  const registerLayer = (el) => {
    const i = openLayers.indexOf(el);
    if (i > -1) openLayers.splice(i, 1);
    openLayers.push(el);
  };
  const unregisterLayer = (el) => {
    const i = openLayers.indexOf(el);
    if (i > -1) openLayers.splice(i, 1);
  };
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !openLayers.length) return;
    const top = openLayers[openLayers.length - 1];
    if (top && typeof top.close === 'function') {
      event.preventDefault();
      top.close();
    }
  });

  // Send focus back to the control that opened a drawer or modal. If that control was replaced
  // (filters and cart re-render), use its visible replacement.
  const returnFocus = (trigger, selector) => {
    let target = trigger && trigger.isConnected ? trigger : null;
    if (!target && selector) {
      target = Array.from(document.querySelectorAll(selector)).find((el) => el.offsetParent !== null) || null;
    }
    target?.focus({ preventScroll: true });
  };

  /* ---------- Overlays and drawers ---------- */
  // Elements can be moved in the theme editor, which runs connectedCallback again.
  // Listeners on the element itself are attached only once.
  const firstConnect = (el) => {
    if (el.ersConnected) return false;
    el.ersConnected = true;
    return true;
  };

  class DrawerPanel extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.overlay = document.querySelector(`[data-overlay-for="${this.id}"]`);
      this.overlay?.addEventListener('click', () => this.close());
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-drawer-close]')) this.close();
      });
    }

    open(trigger) {
      if (this.classList.contains('is-open')) return;
      this.trigger = trigger || document.activeElement;
      if (!this.hasAttribute('role')) {
        this.setAttribute('role', 'dialog');
        this.setAttribute('aria-modal', 'true');
        this.dataset.dynamicRole = 'true';
      }
      this.classList.add('is-primed', 'is-open');
      registerLayer(this);
      this.overlay?.classList.add('is-visible');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'true'));
      lockScroll();
      this.releaseFocus = trapFocus(this);
      requestAnimationFrame(() => {
        const target = this.querySelector('[data-autofocus]') || this.querySelector('[data-drawer-close]') || this;
        target.focus({ preventScroll: true });
      });
      this.dispatchEvent(new CustomEvent('drawer:open'));
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      unregisterLayer(this);
      if (this.dataset.dynamicRole) {
        this.removeAttribute('role');
        this.removeAttribute('aria-modal');
        delete this.dataset.dynamicRole;
      }
      this.overlay?.classList.remove('is-visible');
      document.querySelectorAll(`[aria-controls="${this.id}"]`).forEach((el) => el.setAttribute('aria-expanded', 'false'));
      unlockScroll();
      this.releaseFocus?.();
      returnFocus(this.trigger, `[data-drawer-open="${this.id}"]`);
    }

    disconnectedCallback() {
      // Re-rendered in the theme editor while open: release the page.
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      unregisterLayer(this);
      this.overlay?.classList.remove('is-visible');
      unlockScroll();
      this.releaseFocus?.();
    }
  }
  if (!customElements.get('drawer-panel')) customElements.define('drawer-panel', DrawerPanel);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-drawer-open]');
    if (!opener) return;
    const drawer = document.getElementById(opener.dataset.drawerOpen);
    if (!drawer || typeof drawer.open !== 'function') return;
    event.preventDefault();
    drawer.open(opener);
  });

  /* ---------- Modals ---------- */
  class ModalDialog extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-modal-close]') || event.target.classList.contains('modal__backdrop')) this.close();
      });
      if (this.hasAttribute('data-open-on-load')) this.open();
    }

    open(trigger) {
      this.trigger = trigger || document.activeElement;
      this.classList.add('is-open');
      registerLayer(this);
      this.removeAttribute('aria-hidden');
      lockScroll();
      this.releaseFocus = trapFocus(this);
      this.dispatchEvent(new CustomEvent('modal:open', { detail: { trigger: this.trigger }, bubbles: true }));
      requestAnimationFrame(() => {
        const target = this.querySelector('[data-autofocus]') || this.querySelector('.modal__panel');
        target?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      unregisterLayer(this);
      this.setAttribute('aria-hidden', 'true');
      unlockScroll();
      this.releaseFocus?.();
      returnFocus(this.trigger, `[data-modal-open="${this.id}"]`);
    }

    disconnectedCallback() {
      if (!this.classList.contains('is-open')) return;
      this.classList.remove('is-open');
      unregisterLayer(this);
      unlockScroll();
      this.releaseFocus?.();
    }
  }
  if (!customElements.get('modal-dialog')) customElements.define('modal-dialog', ModalDialog);

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-modal-open]');
    if (!opener) return;
    const modal = document.getElementById(opener.dataset.modalOpen);
    if (!modal) return;
    event.preventDefault();
    modal.open(opener);
  });

  /* ---------- Sticky header + desktop menus ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.header-wrapper') || this.parentElement;
      this.mode = this.dataset.mode || 'smart';
      this.wrapper.dataset.sticky = this.mode;
      this.lastY = window.scrollY;
      this.sub = this.querySelector('[data-header-sub]');
      this.mainBar = this.querySelector('.header__bar--main');
      this.totalHeight = 0;
      this.mainHeight = 0;

      // Heights are read when the browser has laid the header out anyway (no forced layout while the page loads).
      this.resizeObserver?.disconnect();
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.setHeights());
        this.resizeObserver.observe(this);
      } else {
        requestAnimationFrame(() => this.setHeights());
        this.onResize = debounce(() => this.setHeights(), 150);
        window.addEventListener('resize', this.onResize);
      }

      if (this.mode === 'smart') {
        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      this.heroSearch = document.querySelector('[data-hero-search]');
      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) this.closeMenus();
      };
      document.addEventListener('click', this.onDocumentClick);
      if (!firstConnect(this)) return;

      this.querySelectorAll('[data-search-toggle]').forEach((button) =>
        button.addEventListener('click', () => {
          if (this.heroSearch && this.heroSearch.isConnected && this.heroSearch.offsetParent !== null) {
            this.heroSearch.scrollIntoView({ behavior: 'smooth', block: 'center' });
            this.heroSearch.querySelector('input[type="search"]')?.focus({ preventScroll: true });
            return;
          }
          this.setCompact(false);
          this.wrapper.classList.add('is-search-open');
          const input = this.querySelector('.header__search--mobile input[type="search"]');
          input?.focus();
        })
      );
      // The header search opened from the toggle closes again when it's left empty.
      const searchRow = this.querySelector('.header__search--mobile');
      searchRow?.addEventListener('focusout', (event) => {
        if (searchRow.contains(event.relatedTarget)) return;
        const field = searchRow.querySelector('input[type="search"]');
        if (!field || !field.value) this.wrapper.classList.remove('is-search-open');
      });

      this.initMenus();
    }

    // Theme editor: the hero (and its search box) can be added, removed or re-rendered.
    refreshHeroSearch() {
      this.heroSearch = document.querySelector('[data-hero-search]');
      this.observedHeight = null;
      this.observeHeroSearch();
    }

    observeHeroSearch() {
      const desktopSearch = this.querySelector('.header__search--desktop');
      if (!desktopSearch) return;
      // Only rebuild the observer when the header height (the observer's top margin) changes.
      if (this.searchObserver && this.observedHeight === this.totalHeight && this.observedTarget === this.heroSearch) return;
      this.searchObserver?.disconnect();
      this.searchObserver = null;
      this.observedHeight = this.totalHeight;
      this.observedTarget = this.heroSearch;
      if (this.heroSearch && 'IntersectionObserver' in window) {
        this.searchObserver = new IntersectionObserver(
          ([entry]) => desktopSearch.classList.toggle('is-revealed', !entry.isIntersecting),
          { rootMargin: `-${Math.round(this.totalHeight)}px 0px 0px 0px` }
        );
        this.searchObserver.observe(this.heroSearch);
      } else {
        desktopSearch.classList.add('is-revealed');
      }
    }

    disconnectedCallback() {
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      if (this.onDocumentClick) document.removeEventListener('click', this.onDocumentClick);
      this.resizeObserver?.disconnect();
      this.searchObserver?.disconnect();
      this.searchObserver = null;
      this.setCompact(false);
      this.wrapper?.classList.remove('is-search-open');
    }

    // Header heights feed page-wide CSS variables (sticky sidebars, menus). Changing a page-wide
    // variable restyles the whole page, so they're only written when the stylesheet's defaults
    // don't already match the real header.
    setHeights() {
      const total = this.offsetHeight;
      const main = (this.mainBar && this.mainBar.offsetHeight) || total;
      this.totalHeight = total;
      this.mainHeight = main;
      const root = document.documentElement;
      const computed = getComputedStyle(root);
      const writes = [
        ['--header-total-height', `${total}px`],
        ['--header-main-height', `${main}px`],
      ].filter(([name, value]) => computed.getPropertyValue(name).trim() !== value);
      const offset = computed.getPropertyValue('--header-sticky-offset').trim();
      if (this.mode === 'none' && offset !== '0px') writes.push(['--header-sticky-offset', '0px']);
      if (this.mode !== 'none' && root.style.getPropertyValue('--header-sticky-offset')) root.style.removeProperty('--header-sticky-offset');
      writes.forEach(([name, value]) => root.style.setProperty(name, value));
      this.observeHeroSearch();
    }

    // Compact = the second row (search / menu) slides away while scrolling down. Sticky elements
    // follow through a class on <html>, which restyles only those elements.
    setCompact(compact) {
      if (!this.wrapper || this.compact === compact) return;
      this.compact = compact;
      this.wrapper.classList.toggle('is-compact', compact);
      document.documentElement.classList.toggle('header-compact', compact);
    }

    onScroll() {
      const y = window.scrollY;
      const delta = y - this.lastY;
      const threshold = (this.totalHeight || 120) + 40;
      if (this.querySelector('.nav__item.is-open') || this.contains(document.activeElement) && document.activeElement.matches('input')) {
        this.lastY = y;
        return;
      }
      if (y > threshold && delta > 6) {
        this.setCompact(true);
      } else if (delta < -6 || y <= threshold) {
        this.setCompact(false);
      }
      this.lastY = y;
    }

    initMenus() {
      this.items = Array.from(this.querySelectorAll('[data-nav-item]'));
      const hoverCapable = window.matchMedia('(hover: hover)');

      this.items.forEach((item) => {
        const toggle = item.querySelector('[data-nav-toggle]');
        let timer;

        toggle.addEventListener('click', (event) => {
          const isOpen = item.classList.contains('is-open');
          // With a mouse the menu opens on hover, so a click on the open menu keeps it open.
          // Keyboard and touch (event.detail is 0 for keyboard) still toggle.
          if (isOpen && event.detail > 0 && hoverCapable.matches && item.matches(':hover')) return;
          clearTimeout(timer);
          this.closeMenus();
          if (!isOpen) this.openMenu(item);
        });

        // Menu pictures load only once someone heads for the menu, not with every page.
        item.addEventListener('pointerenter', () => item.classList.add('is-primed'));
        toggle.addEventListener('focus', () => item.classList.add('is-primed'));

        item.addEventListener('mouseenter', () => {
          if (!hoverCapable.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => {
            this.closeMenus(item);
            this.openMenu(item);
          }, 90);
        });

        item.addEventListener('mouseleave', () => {
          if (!hoverCapable.matches) return;
          clearTimeout(timer);
          timer = setTimeout(() => this.closeMenu(item), 160);
        });

        item.addEventListener('keydown', (event) => {
          if (event.key === 'Escape' && item.classList.contains('is-open')) {
            this.closeMenu(item);
            toggle.focus();
          }
        });

        item.addEventListener('focusout', (event) => {
          if (!item.contains(event.relatedTarget)) this.closeMenu(item);
        });
      });

    }

    openMenu(item) {
      item.classList.add('is-primed', 'is-open');
      item.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', 'true');
    }

    closeMenu(item) {
      item.classList.remove('is-open');
      item.querySelector('[data-nav-toggle]')?.setAttribute('aria-expanded', 'false');
    }

    closeMenus(except) {
      this.items.forEach((item) => {
        if (item !== except) this.closeMenu(item);
      });
    }
  }
  if (!customElements.get('sticky-header')) customElements.define('sticky-header', StickyHeader);

  /* ---------- Announcement bar ---------- */
  class AnnouncementBar extends HTMLElement {
    connectedCallback() {
      this.items = Array.from(this.querySelectorAll('.announcement__item'));
      if (this.items.length < 2) return;
      this.index = 0;
      this.speed = (Number(this.dataset.speed) || 5) * 1000;
      this.spread = this.querySelector('.announcement__list--spread');
      this.desktop = window.matchMedia(`(min-width: ${Number(this.dataset.spreadMin) || 990}px)`);
      this.paused = false;
      this.pauseButton = this.querySelector('[data-announcement-pause]');
      // Reduced motion: the messages start stopped; the button starts them.
      if (this.stopped === undefined) this.setStopped(window.matchMedia('(prefers-reduced-motion: reduce)').matches);
      clearInterval(this.timer);
      this.timer = setInterval(() => this.next(), this.speed);
      if (!firstConnect(this)) return;
      this.pauseButton?.addEventListener('click', () => this.setStopped(!this.stopped));
      this.addEventListener('mouseenter', () => (this.paused = true));
      this.addEventListener('mouseleave', () => (this.paused = false));
      this.addEventListener('focusin', () => (this.paused = true));
      this.addEventListener('focusout', () => (this.paused = false));
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    setStopped(stopped) {
      this.stopped = stopped;
      this.pauseButton?.setAttribute('aria-pressed', String(stopped));
    }

    next() {
      if (this.paused || this.stopped || document.hidden) return;
      if (this.spread && this.desktop.matches) return;
      this.items[this.index].classList.remove('is-active');
      this.index = (this.index + 1) % this.items.length;
      this.items[this.index].classList.add('is-active');
    }
  }
  if (!customElements.get('announcement-bar')) customElements.define('announcement-bar', AnnouncementBar);

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.input = this.querySelector('input');
      this.querySelectorAll('button').forEach((button) =>
        button.addEventListener('click', (event) => {
          event.preventDefault();
          const step = Number(this.input.step) || 1;
          const min = this.input.min !== '' ? Number(this.input.min) : 0;
          const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
          const current = Number(this.input.value) || 0;
          const next = button.name === 'plus' ? current + step : current - step;
          const clamped = Math.min(Math.max(next, min), max);
          if (clamped === current) return;
          this.input.value = clamped;
          this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.updateButtons();
        })
      );
      this.input.addEventListener('change', () => this.updateButtons());
      this.updateButtons();
    }

    updateButtons() {
      const value = Number(this.input.value) || 0;
      const min = this.input.min !== '' ? Number(this.input.min) : 0;
      const max = this.input.max !== '' ? Number(this.input.max) : Infinity;
      const minus = this.querySelector('[name="minus"]');
      const plus = this.querySelector('[name="plus"]');
      const minLimit = this.dataset.minOne !== undefined ? Math.max(min, 1) : min;
      if (minus) minus.disabled = value <= minLimit;
      if (plus) plus.disabled = value >= max;
    }
  }
  if (!customElements.get('quantity-input')) customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart ---------- */
  const Cart = {
    // Requests run one at a time so quick taps never act on a cart that is about to change.
    queue: Promise.resolve(),

    enqueue(task) {
      const run = Cart.queue.then(task, task);
      Cart.queue = run.catch(() => {});
      return run;
    },

    sectionsFor() {
      const ids = ['cart-drawer'];
      document.querySelectorAll('[data-cart-section]').forEach((el) => ids.push(el.dataset.cartSection));
      return ids;
    },

    async request(url, body) {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        const error = new Error(data.description || data.message || strings.cartError);
        error.data = data;
        throw error;
      }
      return data;
    },

    // Remember what had focus inside a cart so it can be restored after the markup is replaced.
    focusState() {
      const active = document.activeElement;
      if (!active || active === document.body) return null;
      const line = active.closest('[data-key]');
      const control = active.closest('[data-control]');
      const root = active.closest('cart-drawer, [data-cart-section]');
      if (line && control && root) {
        const index = Array.from(root.querySelectorAll('[data-key]')).indexOf(line);
        return { key: line.dataset.key, control: control.dataset.control, index, root };
      }
      if (active.id) return { id: active.id };
      return null;
    },

    restoreFocus(state) {
      if (!state || (document.activeElement && document.activeElement !== document.body)) return;
      let target = null;
      if (state.id) target = document.getElementById(state.id);
      if (!target && state.key && state.root && state.root.isConnected) {
        const lines = Array.from(state.root.querySelectorAll('[data-key]'));
        const line = lines.find((el) => el.dataset.key === state.key);
        if (line) {
          target = line.querySelector(`[data-control="${state.control}"]:not([disabled])`) || line.querySelector('[data-control="qty"]');
        } else {
          // The line was removed: move to the line that took its place, or the cart heading.
          const neighbour = lines[state.index] || lines[state.index - 1];
          target = (neighbour && neighbour.querySelector('[data-control="remove"]')) || state.root.querySelector('[data-cart-focus]') || state.root;
        }
      }
      target?.focus({ preventScroll: true });
    },

    renderSections(sections) {
      if (!sections) return;
      const focus = Cart.focusState();
      const termsChecked = Array.from(document.querySelectorAll('[data-terms-checkbox]')).some((box) => box.checked);
      Object.entries(sections).forEach(([id, html]) => {
        if (!html) return;
        const doc = parseHTML(html);
        if (id === 'cart-drawer') {
          const fresh = doc.querySelector('#CartDrawer');
          const drawer = document.getElementById('CartDrawer');
          if (fresh && drawer) {
            // Keep an order note that's being typed (and its open box) across the refresh.
            const note = drawer.querySelector('[data-cart-note]');
            const noteValue = note ? note.value : null;
            const noteOpen = note && note.closest('details') ? note.closest('details').open : false;
            drawer.innerHTML = fresh.innerHTML;
            const freshNote = drawer.querySelector('[data-cart-note]');
            if (freshNote && noteValue !== null) {
              freshNote.value = noteValue;
              const details = freshNote.closest('details');
              if (details) details.open = noteOpen || noteValue.trim() !== '';
            }
            drawer.dataset.cartCountValue = fresh.dataset.cartCountValue;
            Cart.updateCount(Number(fresh.dataset.cartCountValue), fresh);
          }
          return;
        }
        const target = document.querySelector(`[data-cart-section="${id}"]`);
        const freshSection = doc.querySelector(`[data-cart-section="${id}"]`);
        if (!target || !freshSection) return;
        if (target.querySelector('[data-cart-empty]') && !freshSection.querySelector('[data-cart-empty]')) {
          // The cart page went from empty to filled: reload so the estimator and express checkout buttons start up.
          window.location.reload();
          return;
        }
        const currentRegions = target.querySelectorAll('[data-cart-region]');
        const freshRegions = freshSection.querySelectorAll('[data-cart-region]');
        if (currentRegions.length && currentRegions.length === freshRegions.length) {
          // Swap only the parts that change so express checkout buttons and typed notes stay put.
          freshRegions.forEach((region) => {
            const current = target.querySelector(`[data-cart-region="${region.dataset.cartRegion}"]`);
            if (current) current.innerHTML = region.innerHTML;
          });
        } else {
          target.innerHTML = freshSection.innerHTML;
        }
      });
      if (termsChecked) document.querySelectorAll('[data-terms-checkbox]').forEach((box) => (box.checked = true));
      Cart.restoreFocus(focus);
    },

    // The drawer markup carries a fresh copy of the header badge (digit + screen reader text).
    updateCount(count, freshDrawer) {
      const source = freshDrawer && freshDrawer.querySelector('template[data-cart-count-source]');
      const freshBadge = source && source.content.querySelector('[data-cart-count]');
      document.querySelectorAll('[data-cart-count]').forEach((badge) => {
        if (freshBadge) {
          badge.innerHTML = freshBadge.innerHTML;
          badge.hidden = freshBadge.hasAttribute('hidden');
          return;
        }
        badge.hidden = !count;
        const visible = badge.querySelector('[aria-hidden]');
        if (visible) visible.textContent = Math.min(count, 99);
      });
    },

    change(key, quantity) {
      return Cart.enqueue(async () => {
        const data = await Cart.request(`${routes.cartChange}.js`, {
          id: key,
          quantity,
          sections: Cart.sectionsFor(),
          sections_url: window.location.pathname,
        });
        Cart.renderSections(data.sections);
        return data;
      });
    },
  };
  window.ERS.Cart = Cart;

  class CartDrawer extends DrawerPanel {
    connectedCallback() {
      super.connectedCallback();
      if (!this.overlay) {
        this.overlay = document.createElement('div');
        this.overlay.className = 'overlay';
        this.overlay.dataset.overlayFor = this.id;
        this.after(this.overlay);
        this.overlay.addEventListener('click', () => this.close());
      }
    }
  }
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.addEventListener('change', debounce((event) => this.onChange(event), 300));
      // Enter in a quantity field updates the line instead of submitting the surrounding form.
      this.addEventListener('keydown', (event) => {
        const input = event.target.closest('[data-line-quantity]');
        if (!input || event.key !== 'Enter') return;
        event.preventDefault();
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
      this.addEventListener('click', (event) => {
        const remove = event.target.closest('[data-line-remove]');
        if (!remove) return;
        event.preventDefault();
        const line = remove.closest('[data-key]');
        if (line) this.update(line.dataset.key, 0);
      });
    }

    onChange(event) {
      const input = event.target.closest('[data-line-quantity]');
      const line = input && input.closest('[data-key]');
      if (!line) return;
      this.update(line.dataset.key, Number(input.value));
    }

    async update(key, quantity) {
      const root = this.closest('cart-drawer, [data-cart-section]') || this;
      root.setAttribute('aria-busy', 'true');
      root.classList.add('is-updating');
      try {
        const before = Number(document.getElementById('CartDrawer')?.dataset.cartCountValue || 0);
        const data = await Cart.change(key, quantity);
        if (quantity > 0 && data.items) {
          const item = data.items.find((entry) => entry.key === key);
          if (item && item.quantity < quantity) showToast(strings.quantityError);
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: data, before } }));
      } catch (error) {
        const message = error.message || strings.cartError;
        const line = Array.from(document.querySelectorAll('[data-key]')).find((el) => el.dataset.key === key && root.contains(el));
        const input = line && line.querySelector('[data-line-quantity]');
        if (input) {
          // The cart didn't change, so show the quantity it still has.
          input.value = input.defaultValue;
          input.dispatchEvent(new Event('input', { bubbles: true }));
        }
        const holder = line && line.querySelector('[data-line-error]');
        if (holder) {
          holder.textContent = message;
          holder.hidden = false;
        } else {
          showToast(message);
        }
      } finally {
        root.removeAttribute('aria-busy');
        root.classList.remove('is-updating');
      }
    }
  }
  if (!customElements.get('cart-items')) customElements.define('cart-items', CartItems);

  // "I agree with the terms" checkbox: a clear message when checkout is blocked, kept in sync between drawer and cart page.
  document.addEventListener(
    'invalid',
    (event) => {
      const box = event.target;
      if (box instanceof HTMLInputElement && box.matches('[data-terms-checkbox]')) {
        box.setCustomValidity(box.dataset.message || '');
        box.closest('.cart-terms')?.classList.add('is-invalid');
      }
    },
    true
  );
  document.addEventListener('change', (event) => {
    const box = event.target;
    if (!(box instanceof HTMLInputElement) || !box.matches('[data-terms-checkbox]')) return;
    document.querySelectorAll('[data-terms-checkbox]').forEach((other) => {
      other.checked = box.checked;
      other.setCustomValidity('');
      other.closest('.cart-terms')?.classList.remove('is-invalid');
    });
  });

  // Order note (drawer + cart page)
  document.addEventListener(
    'input',
    debounce((event) => {
      const note = event.target.closest('[data-cart-note]');
      if (!note) return;
      fetch(`${routes.cartUpdate}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: note.value }),
      }).catch(() => {});
    }, 500)
  );

  /* ---------- Add to cart ---------- */
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-product-form]');
    if (!form) return;
    if (theme.cartType === 'page' && !form.hasAttribute('data-ajax')) return;
    event.preventDefault();

    const button = event.submitter || form.querySelector('[type="submit"]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;
    const errorHolder = form.querySelector('[data-form-error]') || form.parentElement.querySelector('[data-form-error]');
    if (errorHolder) errorHolder.hidden = true;

    button.classList.add('is-loading');
    button.setAttribute('aria-disabled', 'true');

    const formData = new FormData(form);
    formData.append('sections', Cart.sectionsFor().join(','));
    formData.append('sections_url', window.location.pathname);

    try {
      const data = await Cart.enqueue(async () => {
        const response = await fetch(`${routes.cartAdd}.js`, {
          method: 'POST',
          headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
          body: formData,
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || strings.cartError);
        Cart.renderSections(result.sections);
        return result;
      });
      document.dispatchEvent(new CustomEvent('cart:added', { detail: { item: data, form } }));

      const drawer = document.getElementById('CartDrawer');
      const cartPage = document.querySelector('[data-cart-section]');
      if (cartPage || (drawer && drawer.classList.contains('is-open'))) {
        // Added from a suggestion inside the cart itself: the cart is already in view and was just
        // re-rendered, so confirm it and keep focus inside the cart.
        showToast(strings.added);
        if (!button.isConnected) {
          const scope = drawer && drawer.classList.contains('is-open') ? drawer : cartPage;
          scope?.querySelector('[data-cart-focus]')?.focus({ preventScroll: true });
        }
        return;
      }
      if (theme.cartType === 'page') {
        window.location.href = routes.cart;
        return;
      }
      if (drawer) {
        drawer.open(button);
      } else {
        showToast(strings.added);
      }
    } catch (error) {
      if (errorHolder) {
        errorHolder.textContent = error.message;
        errorHolder.hidden = false;
      } else {
        showToast(error.message);
      }
    } finally {
      button.classList.remove('is-loading');
      button.removeAttribute('aria-disabled');
    }
  });

  /* ---------- Predictive search ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (!firstConnect(this)) return;
      this.input = this.querySelector('input[type="search"]');
      this.clearButton = this.querySelector('[data-search-clear]');
      this.results = this.querySelector('[data-predictive-results]');
      this.cache = new Map();
      this.enabled = this.hasAttribute('data-predictive') && this.results;

      this.input.addEventListener('input', () => this.toggleClear());
      this.clearButton?.addEventListener('click', () => {
        this.input.value = '';
        this.toggleClear();
        this.close();
        this.input.focus();
      });
      this.toggleClear();

      if (!this.enabled) return;

      this.onInput = debounce(() => this.search(), 220);
      this.input.addEventListener('input', this.onInput);
      this.input.addEventListener('focus', () => {
        if (this.input.value.trim().length >= 2 && this.results.innerHTML.trim()) this.open();
        else if (!this.input.value.trim()) this.showPopular();
      });
      this.input.addEventListener('keydown', (event) => this.onKeydown(event));
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) setTimeout(() => this.close(), 120);
      });
      this.onDocumentClick = (event) => {
        // The header's search icon focuses this box (and opens its list) with the same click.
        if (event.target.closest && event.target.closest('[data-search-toggle]')) return;
        if (this.isConnected && !this.contains(event.target)) this.close();
      };
      document.addEventListener('click', this.onDocumentClick);
    }

    toggleClear() {
      if (this.clearButton) this.clearButton.hidden = !this.input.value;
    }

    // Popular searches (Theme settings > Search) for an empty box.
    showPopular() {
      const template = this.querySelector('template[data-predictive-popular]');
      if (!template) return false;
      this.render(template.innerHTML);
      return true;
    }

    async search() {
      const term = this.input.value.trim();
      if (term.length < 2) {
        this.controller?.abort();
        if (term || !this.showPopular()) this.close();
        return;
      }
      if (this.cache.has(term)) {
        this.render(this.cache.get(term));
        return;
      }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q: term,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,page,article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,variants.sku,vendor,tag,body',
      });
      this.setAttribute('aria-busy', 'true');
      try {
        const response = await fetch(`${routes.predictiveSearch}?${params.toString()}`, { signal: this.controller.signal });
        if (!response.ok) throw new Error(response.status);
        const text = await response.text();
        const html = parseHTML(text).querySelector('[data-predictive-content]')?.outerHTML || '';
        this.cache.set(term, html);
        if (this.input.value.trim() === term) this.render(html);
      } catch (error) {
        if (error.name !== 'AbortError') this.close();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    render(html) {
      if (!html) {
        this.close();
        return;
      }
      this.results.innerHTML = html;
      // Section markup uses fixed ids; prefix them so several search boxes on a page never share ids.
      const prefix = this.results.id;
      this.results.querySelectorAll('[id]').forEach((el) => {
        el.id = `${prefix}-${el.id}`;
      });
      this.results.querySelectorAll('[aria-labelledby]').forEach((el) => {
        el.setAttribute('aria-labelledby', el.getAttribute('aria-labelledby').split(' ').map((id) => `${prefix}-${id}`).join(' '));
      });
      this.options = Array.from(this.results.querySelectorAll('[role="option"]'));
      this.activeIndex = -1;
      this.open();
    }

    open() {
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');
    }

    close() {
      if (!this.results) return;
      this.results.hidden = true;
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
      this.classList.remove('is-open');
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }
      if (event.key === 'ArrowDown' && this.results.hidden) {
        // Reopen what was there before Escape (results, or popular searches for an empty box).
        if (this.input.value.trim().length >= 2 && this.results.innerHTML.trim()) this.open();
        else if (!this.input.value.trim()) this.showPopular();
        if (!this.results.hidden) event.preventDefault();
        return;
      }
      if (this.results.hidden || !this.options?.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const step = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = (this.activeIndex + step + this.options.length) % this.options.length;
        this.options.forEach((option, i) => option.setAttribute('aria-selected', String(i === this.activeIndex)));
        const active = this.options[this.activeIndex];
        this.input.setAttribute('aria-activedescendant', active.id);
        active.scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex > -1) {
        const link = this.options[this.activeIndex];
        if (link && link.href) {
          event.preventDefault();
          window.location.href = link.href;
        }
      }
    }
  }
  if (!customElements.get('predictive-search')) customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Add-on suggestions (complementary products) ---------- */
  // Product page block and cart: the list comes from Shopify's recommendations (intent=complementary),
  // set up per product in the Search & Discovery app. Stays hidden when there's nothing to suggest.
  const addonRequests = new Map();
  class ProductAddons extends HTMLElement {
    connectedCallback() {
      // In the cart drawer, wait until the drawer is opened so closed drawers cost no request.
      const drawer = this.closest('cart-drawer, drawer-panel');
      if (drawer && !drawer.classList.contains('is-open')) {
        if (!this.waitingForDrawer) {
          this.waitingForDrawer = true;
          drawer.addEventListener('drawer:open', () => this.isConnected && this.load(), { once: true });
        }
        return;
      }
      this.load();
    }

    load() {
      const url = this.dataset.url;
      const list = this.querySelector('[data-addons-list]');
      if (!url || !list || this.dataset.state) return;
      this.dataset.state = 'loading';
      if (!addonRequests.has(url)) {
        addonRequests.set(
          url,
          fetch(url)
            .then((response) => (response.ok ? response.text() : ''))
            .catch(() => '')
        );
      }
      addonRequests.get(url).then((html) => {
        if (!this.isConnected) return;
        const exclude = (this.dataset.exclude || '').split(',').filter(Boolean);
        const limit = Number(this.dataset.limit) || 3;
        const items = Array.from(parseHTML(html).querySelectorAll('[data-addons] > li'))
          .filter((item) => !exclude.includes(item.dataset.productId))
          .slice(0, limit);
        this.dataset.state = items.length ? 'ready' : 'empty';
        if (!items.length) return;
        list.replaceChildren(...items.map((item) => document.importNode(item, true)));
        this.hidden = false;
      });
    }
  }
  if (!customElements.get('product-addons')) customElements.define('product-addons', ProductAddons);

  /* ---------- Horizontal rails ---------- */
  class ScrollRail extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-rail-track]');
      this.prev = this.querySelector('[data-rail-prev]');
      this.next = this.querySelector('[data-rail-next]');
      if (!this.track) return;
      this.update();
      // Cards loaded later (recently viewed) replace the items: watch the new first and last ones.
      this.listObserver?.disconnect();
      this.listObserver = new MutationObserver(() => this.update());
      this.listObserver.observe(this.track, { childList: true });
      if (!firstConnect(this)) return;
      const page = (direction) => {
        const amount = this.track.clientWidth * 0.9 * direction;
        this.track.scrollBy({ left: amount, behavior: 'smooth' });
      };
      this.prev?.addEventListener('click', () => page(-1));
      this.next?.addEventListener('click', () => page(1));
    }

    disconnectedCallback() {
      this.edgeObserver?.disconnect();
      this.listObserver?.disconnect();
    }

    // The arrows are disabled when the first or last card is fully in view. An observer reports this
    // without reading scroll positions on every scroll.
    update() {
      this.edgeObserver?.disconnect();
      const items = this.track.children;
      if (!items.length || !('IntersectionObserver' in window)) {
        if (this.prev) this.prev.disabled = !items.length;
        if (this.next) this.next.disabled = !items.length;
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      this.edgeObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const inView = entry.isIntersecting && entry.intersectionRatio >= 0.97;
            if (entry.target === first && this.prev) this.prev.disabled = inView;
            if (entry.target === last && this.next) this.next.disabled = inView;
          });
        },
        { root: this.track, threshold: [0, 0.97, 1] }
      );
      this.edgeObserver.observe(first);
      if (last !== first) this.edgeObserver.observe(last);
    }
  }
  if (!customElements.get('scroll-rail')) customElements.define('scroll-rail', ScrollRail);

  /* ---------- Image banner slideshow ---------- */
  class SlideShow extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-slides]');
      if (!this.track) return;
      this.slides = Array.from(this.track.children);
      if (this.slides.length < 2) return;
      if (this.ersConnected) {
        // Moved in the theme editor: listeners are in place, restart the timer and observer.
        if (this.autoplay) {
          document.addEventListener('visibilitychange', this.onVisibility);
          this.observer?.observe(this);
          this.schedule();
        }
        return;
      }
      this.ersConnected = true;
      this.dots = Array.from(this.querySelectorAll('[data-slide-dot]'));
      this.toggle = this.querySelector('[data-slide-toggle]');
      this.index = 0;
      this.holds = new Set();
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this.querySelector('[data-slide-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
      this.querySelector('[data-slide-next]')?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(Number(dot.dataset.slideDot))));
      this.track.addEventListener(
        'scroll',
        debounce(() => {
          const i = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
          if (i !== this.index && this.slides[i]) this.setActive(i);
        }, 80),
        { passive: true }
      );

      // Theme editor: show the selected banner and hold the slideshow while it's selected.
      this.addEventListener('shopify:block:select', (event) => {
        const i = this.slides.indexOf(event.target);
        if (i > -1) this.goTo(i, { manual: false, smooth: false });
        this.setHold('editor', true);
      });
      this.addEventListener('shopify:block:deselect', () => this.setHold('editor', false));

      this.autoplay = this.dataset.autoplay === 'true' && !this.reducedMotion;
      if (!this.autoplay) {
        this.track.setAttribute('aria-live', 'polite');
        return;
      }
      this.speed = Math.max(3, Number(this.dataset.speed) || 7) * 1000;
      if (this.toggle) {
        this.toggle.hidden = false;
        this.toggle.addEventListener('click', () => {
          if (this.holds.has('user')) {
            // An explicit "Play" restarts rotation even though focus is on the button.
            this.holds.delete('focus');
            this.setHold('user', false);
          } else {
            this.setHold('user', true);
          }
        });
      }
      this.addEventListener('mouseenter', () => this.setHold('hover', true));
      this.addEventListener('mouseleave', () => this.setHold('hover', false));
      this.addEventListener('focusin', (event) => {
        if (event.target !== this.toggle) this.setHold('focus', true);
      });
      this.addEventListener('focusout', (event) => {
        if (!this.contains(event.relatedTarget)) this.setHold('focus', false);
      });
      this.track.addEventListener('touchstart', () => this.setHold('user', true), { passive: true });
      this.onVisibility = () => this.schedule();
      document.addEventListener('visibilitychange', this.onVisibility);
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(([entry]) => this.setHold('offscreen', !entry.isIntersecting));
        this.observer.observe(this);
      }
      this.schedule();
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.observer?.disconnect();
      if (this.onVisibility) document.removeEventListener('visibilitychange', this.onVisibility);
    }

    setHold(reason, on) {
      if (on) this.holds.add(reason);
      else this.holds.delete(reason);
      if (this.toggle) {
        const paused = this.holds.has('user');
        this.toggle.classList.toggle('is-paused', paused);
        this.toggle.setAttribute('aria-label', paused ? this.toggle.dataset.labelPlay : this.toggle.dataset.labelPause);
      }
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      const running = this.autoplay && this.holds.size === 0 && !document.hidden;
      this.track.setAttribute('aria-live', running ? 'off' : 'polite');
      if (!running) return;
      this.timer = setTimeout(() => {
        this.goTo(this.index + 1, { manual: false });
        this.schedule();
      }, this.speed);
    }

    goTo(i, { manual = true, smooth = true } = {}) {
      const count = this.slides.length;
      const index = ((i % count) + count) % count;
      this.track.scrollTo({ left: this.track.clientWidth * index, behavior: smooth && !this.reducedMotion ? 'smooth' : 'auto' });
      this.setActive(index);
      if (manual && this.autoplay) this.setHold('user', true);
    }

    setActive(index) {
      this.index = index;
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.dots.forEach((dot, i) => {
        if (i === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }
  }
  if (!customElements.get('slide-show')) customElements.define('slide-show', SlideShow);

  /* ---------- Account panel position (Shopify's <shopify-account> component) ---------- */
  document.addEventListener(
    'open',
    (event) => {
      if (!(event.target instanceof HTMLElement) || !event.target.matches('shopify-account')) return;
      const bottom = Math.max(0, Math.round(event.target.getBoundingClientRect().bottom + 8));
      event.target.style.setProperty('--account-dialog-top', `${bottom}px`);
    },
    true
  );

  /* ---------- Contact form prefill (?message=...) ---------- */
  // ?item=... (from a product's "Have one to sell?" link) starts the sell form's equipment list.
  document.addEventListener('DOMContentLoaded', () => {
    const params = new URLSearchParams(window.location.search);
    const message = (params.get('message') || '').slice(0, 3000);
    const item = (params.get('item') || '').slice(0, 300);
    if (message) {
      document.querySelectorAll('[data-prefill-message]').forEach((field) => {
        if (!field.value) field.value = message;
      });
    }
    if (item) {
      document.querySelectorAll('[data-prefill-item]').forEach((field) => {
        if (!field.value) field.value = (strings.sellItem || '[item]').replace('[item]', item);
      });
    }
  });

  /* ---------- Copy link buttons ---------- */
  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-copy-link]');
    if (!button) return;
    event.preventDefault();
    const url = button.dataset.copyLink || window.location.href;
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).then(() => showToast(strings.copied)).catch(() => {});
    }
  });

  /* ---------- Keep the cart current on pages shown from the back/forward cache ---------- */
  // A page kept for the Back button can show a cart from before the latest change. When the cart has
  // changed since the page was left, ask Shopify for fresh cart markup.
  const CART_STAMP = 'ers-cart-changed';
  const markCartChanged = () => {
    try {
      sessionStorage.setItem(CART_STAMP, String(Date.now()));
    } catch (e) {}
  };
  document.addEventListener('cart:updated', markCartChanged);
  document.addEventListener('cart:added', markCartChanged);
  document.addEventListener('input', (event) => {
    if (event.target.closest && event.target.closest('[data-cart-note]')) markCartChanged();
  });
  let pageHiddenAt = 0;
  window.addEventListener('pagehide', () => {
    pageHiddenAt = Date.now();
  });
  window.addEventListener('pageshow', (event) => {
    let changedAt = 0;
    try {
      changedAt = Number(sessionStorage.getItem(CART_STAMP)) || 0;
    } catch (e) {}
    if (!changedAt) return;
    // Only pages restored by the Back button need this: Shopify's cart responses already tell the
    // browser to drop pages it loaded ahead of a click (Clear-Site-Data: "prefetchCache").
    if (!event.persisted || changedAt <= pageHiddenAt) return;
    const ids = Cart.sectionsFor();
    fetch(`${window.location.pathname}?sections=${encodeURIComponent(ids.join(','))}`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .then((sections) => sections && Cart.renderSections(sections))
      .catch(() => {});
  });

  /* ---------- Theme editor: keep the header in step with the hero search box ---------- */
  if (window.Shopify && window.Shopify.designMode) {
    const syncHeroSearch = () => {
      document.documentElement.classList.toggle('has-hero-search', !!document.querySelector('[data-hero-search]'));
      document.querySelectorAll('sticky-header').forEach((header) => header.refreshHeroSearch?.());
    };
    document.addEventListener('shopify:section:load', syncHeroSearch);
    document.addEventListener('shopify:section:unload', () => setTimeout(syncHeroSearch, 0));
  }
})();
